import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Clock, Bookmark, Share2, ExternalLink } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { de } from 'date-fns/locale';
import { Button } from '../components/ui/button';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { getCategoryName, getRegionName } from '../utils/labels';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function NewsDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const [news, setNews] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isBookmarked, setIsBookmarked] = useState(false);

  useEffect(() => {
    fetchNews();
  }, [id]);

  useEffect(() => {
    if (user && news) {
      setIsBookmarked(user.bookmarks.includes(news.id));
    }
  }, [user, news]);

  const fetchNews = async () => {
    try {
      const response = await axios.get(`${API}/news/${id}`);
      setNews(response.data);
    } catch (error) {
      console.error('Failed to fetch news:', error);
      toast.error('Nachricht konnte nicht geladen werden');
    } finally {
      setLoading(false);
    }
  };

  const handleBookmark = async () => {
    if (!user) {
      toast.error('Bitte melden Sie sich an, um Lesezeichen zu setzen');
      return;
    }

    try {
      if (isBookmarked) {
        await axios.delete(`${API}/bookmarks/${news.id}`);
        toast.success('Lesezeichen entfernt');
        setIsBookmarked(false);
      } else {
        await axios.post(`${API}/bookmarks`, { news_id: news.id });
        toast.success('Lesezeichen gespeichert');
        setIsBookmarked(true);
      }
    } catch (error) {
      console.error('Bookmark error:', error);
      toast.error('Fehler beim Speichern');
    }
  };

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: news.title,
          text: news.summary,
          url: window.location.href
        });
      } catch (error) {
        console.error('Share error:', error);
      }
    } else {
      navigator.clipboard.writeText(window.location.href);
      toast.success('Link kopiert!');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen" data-testid="loading-indicator">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0055FF]"></div>
      </div>
    );
  }

  if (!news) {
    return (
      <div className="min-h-screen bg-[#FAFAFA] dark:bg-[#0A0A0A] flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold mb-4">Nachricht nicht gefunden</h2>
          <Link to="/" className="text-[#0055FF] hover:underline">Zurück zur Startseite</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAFAFA] dark:bg-[#0A0A0A] py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Back Button */}
        <Link
          to="/"
          className="inline-flex items-center text-[#52525B] dark:text-[#A1A1AA] hover:text-[#0055FF] dark:hover:text-[#3377FF] mb-8 transition-colors"
          data-testid="back-button"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Zurück
        </Link>

        {/* Article */}
        <article className="bg-white dark:bg-[#121212] rounded-2xl border border-black/10 dark:border-white/10 overflow-hidden" data-testid="news-article">
          {news.image_url && (
            <img
              src={news.image_url}
              alt={news.title}
              className="w-full h-96 object-cover"
            />
          )}

          <div className="p-8">
            {/* Meta */}
            <div className="flex flex-wrap items-center gap-3 mb-6">
              <span className="bg-[#0055FF] text-white px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider">
                {getCategoryName(news.category)}
              </span>
              {news.is_breaking && (
                <span className="bg-red-500 text-white px-4 py-1.5 rounded-full text-xs font-bold">
                  BREAKING
                </span>
              )}
              <span className="text-sm text-[#52525B] dark:text-[#A1A1AA]">
                {getRegionName(news.region)}
              </span>
            </div>

            {/* Title */}
            <h1 className="text-4xl sm:text-5xl font-extrabold tracking-tight text-[#09090B] dark:text-[#FAFAFA] mb-6">
              {news.title}
            </h1>

            {/* Time & Actions */}
            <div className="flex items-center justify-between mb-8 pb-6 border-b border-black/10 dark:border-white/10">
              <div className="flex items-center space-x-4 text-sm text-[#52525B] dark:text-[#A1A1AA]">
                <span className="flex items-center">
                  <Clock className="h-4 w-4 mr-1" />
                  {formatDistanceToNow(new Date(news.published_at), { addSuffix: true, locale: de })}
                </span>
                <span>{news.read_time}s Lesezeit</span>
                <span>{news.view_count} Aufrufe</span>
              </div>

              <div className="flex items-center space-x-2">
                <Button
                  variant="outline"
                  size="icon"
                  onClick={handleBookmark}
                  className="rounded-full"
                  data-testid="bookmark-button"
                >
                  <Bookmark className={`h-4 w-4 ${isBookmarked ? 'fill-current' : ''}`} />
                </Button>
                <Button
                  variant="outline"
                  size="icon"
                  onClick={handleShare}
                  className="rounded-full"
                  data-testid="share-button"
                >
                  <Share2 className="h-4 w-4" />
                </Button>
              </div>
            </div>

            {/* Summary */}
            <div className="prose prose-lg dark:prose-invert max-w-none mb-8">
              <p className="text-lg leading-relaxed text-[#09090B] dark:text-[#FAFAFA]">
                {news.summary}
              </p>
              {news.content && (
                <p className="text-base leading-relaxed text-[#52525B] dark:text-[#A1A1AA] mt-4">
                  {news.content}
                </p>
              )}
            </div>

            {/* Tags */}
            {news.tags && news.tags.length > 0 && (
              <div className="mb-8">
                <h3 className="text-sm font-bold uppercase tracking-wider text-[#52525B] dark:text-[#A1A1AA] mb-3">
                  Tags
                </h3>
                <div className="flex flex-wrap gap-2">
                  {news.tags.map((tag) => (
                    <span
                      key={`tag-${tag}`}
                      className="px-3 py-1 bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 rounded-full text-sm"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Sources */}
            {news.sources && news.sources.length > 0 && (
              <div>
                <h3 className="text-sm font-bold uppercase tracking-wider text-[#52525B] dark:text-[#A1A1AA] mb-3">
                  Quellen
                </h3>
                <div className="space-y-2">
                  {news.sources.map((source) => (
                    <a
                      key={`source-${source.url || source.name}`}
                      href={source.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center text-[#0055FF] dark:text-[#3377FF] hover:underline"
                    >
                      <ExternalLink className="h-3 w-3 mr-2" />
                      {source.name}
                    </a>
                  ))}
                </div>
              </div>
            )}
          </div>
        </article>
      </div>
    </div>
  );
}

export default NewsDetail;
